import type { PlayerCri, TeamCode } from '../types/neuro'

export const neuroTeams: Record<
  TeamCode,
  { name: string; textClass: string; dotClass: string; logo: string }
> = {
  LAL: {
    name: 'Los Angeles Lakers',
    textClass: 'text-[#2563EB]',
    dotClass: 'bg-[#2563EB]',
    logo: 'https://cdn.nba.com/logos/nba/1610612747/global/L/logo.svg',
  },
  NYK: {
    name: 'New York Knicks',
    textClass: 'text-[#DC2626]',
    dotClass: 'bg-[#DC2626]',
    logo: 'https://cdn.nba.com/logos/nba/1610612752/global/L/logo.svg',
  },
}

export const teamOrder: TeamCode[] = ['LAL', 'NYK']

export const neuroPlayers: PlayerCri[] = [
  {
    player_id: 'lal-okafor',
    name: 'T. Okafor',
    position: 'F',
    team: 'LAL',
    minutes_played: 22.3,
    n_player_events: 40,
    confidence: 0.91,
    cri_match: 83,
    fi_match: 74,
    ii_match: 19,
    rei_match: 100,
    fi_q1: 63.8,
    fi_q2: 75.0,
    fi_q3: 78.1,
    fi_q4: 75.4,
    time_pct_ready: 67,
    time_pct_alert: 22,
    time_pct_declining: 9,
    time_pct_off: 2,
    loops_reached: 0,
    rei_state_final: 'Ready',
    error_to_first_positive: 0.8,
    confidence_flag: 'Sufficient',
    report_text:
      'T. Okafor (CRI 83) showed a high level of cognitive readiness throughout the game, peaking in the middle quarters before fading slightly. The standout figure is the Reaction index (100): the player never entered a perseverative loop and bounced back from mistakes within one or two actions. 67% of court time was spent in the Ready state, indicating stable cognition across the full game.',
  },
  {
    player_id: 'lal-marin',
    name: 'D. Marin',
    position: 'G',
    team: 'LAL',
    minutes_played: 14.9,
    n_player_events: 29,
    confidence: 0.82,
    cri_match: 74,
    fi_match: 63,
    ii_match: 19,
    rei_match: 80,
    fi_q1: 58.2,
    fi_q2: 64.5,
    fi_q3: 66.0,
    fi_q4: 63.1,
    time_pct_ready: 58,
    time_pct_alert: 26,
    time_pct_declining: 12,
    time_pct_off: 4,
    loops_reached: 0,
    rei_state_final: 'Ready',
    error_to_first_positive: 1.4,
    confidence_flag: 'Sufficient',
    report_text:
      'D. Marin (CRI 74) played with solid readiness in a bench role. Focus built steadily after a slower first quarter and held through the second half. Recovery after errors was quick (about 1.4 actions), and no off-state loops were reached, so lapses stayed short and contained.',
  },
  {
    player_id: 'lal-lindqvist',
    name: 'S. Lindqvist',
    position: 'G',
    team: 'LAL',
    minutes_played: 16.6,
    n_player_events: 42,
    confidence: 0.88,
    cri_match: 71,
    fi_match: 70,
    ii_match: 12,
    rei_match: 49,
    fi_q1: 71.4,
    fi_q2: 72.8,
    fi_q3: 68.9,
    fi_q4: 66.5,
    time_pct_ready: 52,
    time_pct_alert: 28,
    time_pct_declining: 15,
    time_pct_off: 5,
    loops_reached: 1,
    rei_state_final: 'Alert',
    error_to_first_positive: 2.6,
    confidence_flag: 'Sufficient',
    report_text:
      'S. Lindqvist (CRI 71) combined strong focus with very low impulsivity (12), making disciplined decisions throughout. The weaker area is Reaction (49): after mistakes the player took around 2–3 actions to produce a positive play, and one off-state loop occurred late in the third quarter. Focus eased slightly in the second half.',
  },
  {
    player_id: 'lal-bellamy',
    name: 'R. Bellamy',
    position: 'F',
    team: 'LAL',
    minutes_played: 10.2,
    n_player_events: 12,
    confidence: 0.58,
    cri_match: 51,
    fi_match: 49,
    ii_match: 11,
    rei_match: 0,
    fi_q1: 55.0,
    fi_q2: 50.3,
    fi_q3: 46.1,
    fi_q4: 44.6,
    time_pct_ready: 24,
    time_pct_alert: 30,
    time_pct_declining: 28,
    time_pct_off: 18,
    loops_reached: 3,
    rei_state_final: 'Off',
    error_to_first_positive: 6.0,
    confidence_flag: 'Limited',
  },
  {
    player_id: 'nyk-duarte',
    name: 'K. Duarte',
    position: 'G',
    team: 'NYK',
    minutes_played: 31.2,
    n_player_events: 58,
    confidence: 0.9,
    cri_match: 78,
    fi_match: 72,
    ii_match: 22,
    rei_match: 88,
    fi_q1: 69.5,
    fi_q2: 73.2,
    fi_q3: 74.0,
    fi_q4: 71.3,
    time_pct_ready: 63,
    time_pct_alert: 24,
    time_pct_declining: 10,
    time_pct_off: 3,
    loops_reached: 0,
    rei_state_final: 'Ready',
    error_to_first_positive: 0.9,
    confidence_flag: 'Sufficient',
    report_text:
      'K. Duarte (CRI 78) carried a heavy load and stayed cognitively sharp throughout. Reaction (88) is the key strength: errors were followed by a positive action almost immediately, and no off-state loops were recorded. Focus was consistent across all four quarters, with only a small dip in the closing minutes.',
  },
  {
    player_id: 'nyk-novak',
    name: 'A. Novak',
    position: 'C',
    team: 'NYK',
    minutes_played: 28.4,
    n_player_events: 51,
    confidence: 0.93,
    cri_match: 66,
    fi_match: 61,
    ii_match: 31,
    rei_match: 72,
    fi_q1: 64.0,
    fi_q2: 62.2,
    fi_q3: 59.1,
    fi_q4: 58.7,
    time_pct_ready: 45,
    time_pct_alert: 33,
    time_pct_declining: 17,
    time_pct_off: 5,
    loops_reached: 1,
    rei_state_final: 'Alert',
    error_to_first_positive: 1.9,
    confidence_flag: 'Sufficient',
    report_text:
      'A. Novak (CRI 66) showed moderate readiness with a gradual decline in focus after halftime. Impulsivity (31) was the highest among the Knicks starters, mostly in contested rebounding situations. Recovery from errors stayed reasonable, with one short off-state loop in the third quarter.',
  },
  {
    player_id: 'nyk-whitfield',
    name: 'L. Whitfield',
    position: 'F',
    team: 'NYK',
    minutes_played: 19.5,
    n_player_events: 33,
    confidence: 0.79,
    cri_match: 58,
    fi_match: 55,
    ii_match: 27,
    rei_match: 60,
    fi_q1: 57.8,
    fi_q2: 56.1,
    fi_q3: 53.4,
    fi_q4: 52.7,
    time_pct_ready: 38,
    time_pct_alert: 34,
    time_pct_declining: 21,
    time_pct_off: 7,
    loops_reached: 1,
    rei_state_final: 'Declining',
    error_to_first_positive: 2.4,
    confidence_flag: 'Sufficient',
    report_text:
      'L. Whitfield (CRI 58) spent most of the game between the Ready and Alert states, ending in Declining. Focus slipped a little each quarter. Reaction (60) was average: the player usually recovered within two to three actions after an error.',
  },
  {
    player_id: 'nyk-haddad',
    name: 'P. Haddad',
    position: 'G',
    team: 'NYK',
    minutes_played: 8.7,
    n_player_events: 14,
    confidence: 0.62,
    cri_match: 44,
    fi_match: 42,
    ii_match: 38,
    rei_match: 35,
    fi_q1: 47.5,
    fi_q2: 43.0,
    fi_q3: 40.2,
    fi_q4: 37.3,
    time_pct_ready: 22,
    time_pct_alert: 31,
    time_pct_declining: 30,
    time_pct_off: 17,
    loops_reached: 2,
    rei_state_final: 'Declining',
    error_to_first_positive: 3.8,
    confidence_flag: 'Limited',
    report_text:
      'P. Haddad (CRI 44) had low readiness in limited minutes. Focus declined across each stint and impulsivity was elevated (38), with two off-state loops reached. With only 14 events, treat these values as indicative rather than conclusive.',
  },
  // Additional analyzed players (hidden by default, available via Show all / Add player)
  {
    player_id: 'lal-castellano',
    name: 'J. Castellano',
    position: 'G',
    team: 'LAL',
    minutes_played: 18.4,
    n_player_events: 34,
    confidence: 0.85,
    cri_match: 68,
    fi_match: 64,
    ii_match: 24,
    rei_match: 70,
    fi_q1: 62.1,
    fi_q2: 66.0,
    fi_q3: 65.2,
    fi_q4: 62.8,
    time_pct_ready: 47,
    time_pct_alert: 31,
    time_pct_declining: 16,
    time_pct_off: 6,
    loops_reached: 1,
    rei_state_final: 'Alert',
    error_to_first_positive: 1.8,
    confidence_flag: 'Sufficient',
    report_text:
      'J. Castellano (CRI 68) delivered moderate readiness with a stable middle of the game. Reaction (70) was solid, with recovery after errors in under two actions on average. One short off-state loop occurred in the fourth quarter.',
  },
  {
    player_id: 'lal-ferreira',
    name: 'M. Ferreira',
    position: 'C',
    team: 'LAL',
    minutes_played: 20.1,
    n_player_events: 37,
    confidence: 0.87,
    cri_match: 62,
    fi_match: 58,
    ii_match: 29,
    rei_match: 66,
    fi_q1: 60.4,
    fi_q2: 59.0,
    fi_q3: 57.2,
    fi_q4: 55.6,
    time_pct_ready: 42,
    time_pct_alert: 33,
    time_pct_declining: 18,
    time_pct_off: 7,
    loops_reached: 1,
    rei_state_final: 'Alert',
    error_to_first_positive: 2.1,
    confidence_flag: 'Sufficient',
    report_text:
      'M. Ferreira (CRI 62) started well but focus eased a little each quarter. Impulsivity (29) showed mostly in early help-defense rotations. Recovery from errors stayed within about two actions.',
  },
  {
    player_id: 'lal-brandt',
    name: 'O. Brandt',
    position: 'F',
    team: 'LAL',
    minutes_played: 12.7,
    n_player_events: 21,
    confidence: 0.76,
    cri_match: 55,
    fi_match: 52,
    ii_match: 33,
    rei_match: 58,
    fi_q1: 54.0,
    fi_q2: 53.1,
    fi_q3: 51.0,
    fi_q4: 49.9,
    time_pct_ready: 35,
    time_pct_alert: 34,
    time_pct_declining: 22,
    time_pct_off: 9,
    loops_reached: 1,
    rei_state_final: 'Declining',
    error_to_first_positive: 2.7,
    confidence_flag: 'Sufficient',
    report_text:
      'O. Brandt (CRI 55) played short stints with moderate readiness. Impulsivity (33) was on the high side and focus drifted down through the second half.',
  },
  {
    player_id: 'lal-sato',
    name: 'E. Sato',
    position: 'G',
    team: 'LAL',
    minutes_played: 6.3,
    n_player_events: 13,
    confidence: 0.55,
    cri_match: 47,
    fi_match: 45,
    ii_match: 36,
    rei_match: 18,
    fi_q1: 48.2,
    fi_q2: 46.0,
    fi_q3: 44.1,
    fi_q4: 41.7,
    time_pct_ready: 20,
    time_pct_alert: 30,
    time_pct_declining: 31,
    time_pct_off: 19,
    loops_reached: 3,
    rei_state_final: 'Off',
    error_to_first_positive: 5.2,
    confidence_flag: 'Limited',
  },
  {
    player_id: 'nyk-adeyemi',
    name: 'N. Adeyemi',
    position: 'F',
    team: 'NYK',
    minutes_played: 24.6,
    n_player_events: 44,
    confidence: 0.89,
    cri_match: 73,
    fi_match: 68,
    ii_match: 20,
    rei_match: 79,
    fi_q1: 66.2,
    fi_q2: 69.4,
    fi_q3: 69.0,
    fi_q4: 67.3,
    time_pct_ready: 56,
    time_pct_alert: 27,
    time_pct_declining: 13,
    time_pct_off: 4,
    loops_reached: 0,
    rei_state_final: 'Ready',
    error_to_first_positive: 1.2,
    confidence_flag: 'Sufficient',
    report_text:
      'N. Adeyemi (CRI 73) stayed ready across all four quarters with low impulsivity (20). No off-state loops were reached and mistakes were followed by a positive play quickly.',
  },
  {
    player_id: 'nyk-kowalski',
    name: 'V. Kowalski',
    position: 'C',
    team: 'NYK',
    minutes_played: 15.8,
    n_player_events: 27,
    confidence: 0.8,
    cri_match: 61,
    fi_match: 57,
    ii_match: 28,
    rei_match: 64,
    fi_q1: 58.9,
    fi_q2: 57.6,
    fi_q3: 56.0,
    fi_q4: 55.4,
    time_pct_ready: 41,
    time_pct_alert: 34,
    time_pct_declining: 18,
    time_pct_off: 7,
    loops_reached: 1,
    rei_state_final: 'Alert',
    error_to_first_positive: 2.2,
    confidence_flag: 'Sufficient',
    report_text:
      'V. Kowalski (CRI 61) showed moderate readiness in a backup role, with steady but unspectacular focus and average recovery after errors.',
  },
  {
    player_id: 'nyk-montoya',
    name: 'B. Montoya',
    position: 'G',
    team: 'NYK',
    minutes_played: 11.3,
    n_player_events: 19,
    confidence: 0.71,
    cri_match: 53,
    fi_match: 50,
    ii_match: 34,
    rei_match: 55,
    fi_q1: 52.8,
    fi_q2: 51.0,
    fi_q3: 49.3,
    fi_q4: 47.0,
    time_pct_ready: 31,
    time_pct_alert: 35,
    time_pct_declining: 24,
    time_pct_off: 10,
    loops_reached: 1,
    rei_state_final: 'Declining',
    error_to_first_positive: 2.9,
    confidence_flag: 'Limited',
  },
  {
    player_id: 'nyk-strand',
    name: 'H. Strand',
    position: 'F',
    team: 'NYK',
    minutes_played: 9.1,
    n_player_events: 15,
    confidence: 0.66,
    cri_match: 49,
    fi_match: 47,
    ii_match: 30,
    rei_match: 41,
    fi_q1: 50.6,
    fi_q2: 48.1,
    fi_q3: 45.9,
    fi_q4: 43.4,
    time_pct_ready: 27,
    time_pct_alert: 33,
    time_pct_declining: 27,
    time_pct_off: 13,
    loops_reached: 2,
    rei_state_final: 'Declining',
    error_to_first_positive: 3.5,
    confidence_flag: 'Limited',
    report_text:
      'H. Strand (CRI 49) had low readiness in limited minutes, with focus slipping in each stint and two off-state loops. Treat as indicative given the small sample.',
  },
]

/** Players shown in each team section before the user expands or adds more. */
export const defaultVisibleIds: string[] = [
  'lal-okafor',
  'lal-marin',
  'lal-lindqvist',
  'lal-bellamy',
  'nyk-duarte',
  'nyk-novak',
  'nyk-whitfield',
  'nyk-haddad',
]
